import { useEffect, useMemo, useState } from 'react';
import { useClientes, useVendasLedger } from '@/hooks/useDB';

function formatKg(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value);
}

function monthKey(dataIso: string) {
  return dataIso.slice(0, 7); // YYYY-MM
}

function monthLabel(key: string) {
  const [year, month] = key.split('-');
  return new Date(Number(year), Number(month) - 1, 1)
    .toLocaleDateString('pt-BR', { month: 'short' })
    .replace('.', '')
    .toUpperCase();
}

type Status = 'verde' | 'amarelo' | 'vermelho';

const STATUS_META: Record<Status, { label: string; dot: string; icon: string; text: string }> = {
  verde: { label: 'Vendas OK', dot: 'bg-emerald-500', icon: 'ri-emotion-happy-line', text: 'text-emerald-600' },
  amarelo: { label: 'Vendas reduziram no último mês', dot: 'bg-amber-400', icon: 'ri-emotion-normal-line', text: 'text-amber-600' },
  vermelho: { label: 'Cliente sem compras no último mês', dot: 'bg-red-500', icon: 'ri-emotion-unhappy-line', text: 'text-red-600' },
};

interface SalesHealthPanelProps {
  dataInicial: string;
  dataFinal: string;
  vendedor: string;
  cliente: string;
  produto: string;
  allowedPeople: string[] | null;
}

function normalize(value?: string) {
  return (value || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();
}

function normalizeSearch(value?: string) {
  return normalize(value).replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
}

interface ClienteLinha {
  codigo: string;
  nome: string;
  uf: string;
  responsavel: string;
  byMonth: Record<string, number>;
  total: number;
  indicador: Status;
}

function matchesPerson(left?: string, right?: string) {
  const first = normalize(left);
  const second = normalize(right);
  if (!first || !second) return false;
  return first === second;
}

export default function SalesHealthPanel({ dataInicial, dataFinal, vendedor, cliente: clienteFiltro, produto: produtoFiltro, allowedPeople }: SalesHealthPanelProps) {
  const { todosClientes: clientes } = useClientes();
  const { vendas, loading } = useVendasLedger();
  const [pagina, setPagina] = useState(1);

  const { meses, linhas, clientesAtivos, compraramUltimoMes, variacao, totalPorMes, contagemStatus } = useMemo(() => {
    const clientesMap = new Map(clientes.map((c) => [c.codigo, c]));
    const vendasFiltradas = vendas.filter((v) => {
      const cliente = clientesMap.get(v.codCliente);
      if (!cliente) return false;
      if (allowedPeople !== null && !allowedPeople.some((name) => matchesPerson(cliente.responsavel, name))) return false;
      if (vendedor && !normalize(cliente.responsavel).includes(normalize(vendedor))) return false;
      if (clienteFiltro && !normalizeSearch(`${v.codCliente} ${v.cliente} ${cliente.nome}`).includes(normalizeSearch(clienteFiltro))) return false;
      if (produtoFiltro && !normalizeSearch(`${v.codMaterial} ${v.material}`).includes(normalizeSearch(produtoFiltro))) return false;
      if (dataInicial && v.dataEmissao < dataInicial) return false;
      if (dataFinal && v.dataEmissao > dataFinal) return false;
      return true;
    });

    const porCliente = new Map<string, ClienteLinha>();
    const mesesSet = new Set<string>();
    for (const v of vendasFiltradas) {
      const cliente = clientesMap.get(v.codCliente)!;
      const key = monthKey(v.dataEmissao);
      mesesSet.add(key);
      const existing = porCliente.get(v.codCliente);
      if (existing) {
        existing.byMonth[key] = (existing.byMonth[key] || 0) + v.qtde;
        existing.total += v.qtde;
      } else {
        porCliente.set(v.codCliente, {
          codigo: v.codCliente,
          nome: cliente.nome,
          uf: cliente.uf,
          responsavel: cliente.responsavel || '',
          byMonth: { [key]: v.qtde },
          total: v.qtde,
          indicador: 'verde',
        });
      }
    }

    const meses = [...mesesSet].sort().slice(-6);
    const lastMes = meses.at(-1);
    const prevMes = meses.at(-2);

    let compraramUltimoMes = 0;
    const contagemStatus: Record<Status, number> = { verde: 0, amarelo: 0, vermelho: 0 };
    const linhas = [...porCliente.values()].map((linha) => {
      const lastVal = lastMes ? linha.byMonth[lastMes] || 0 : 0;
      const prevVal = prevMes ? linha.byMonth[prevMes] || 0 : 0;
      let indicador: Status = 'verde';
      if (lastMes && lastVal <= 0) indicador = 'vermelho';
      else if (prevMes && prevVal > 0 && lastVal < prevVal) indicador = 'amarelo';
      linha.indicador = indicador;
      contagemStatus[indicador] += 1;
      if (lastVal > 0) compraramUltimoMes += 1;
      return linha;
    }).filter((linha) => linha.total > 0).sort((a, b) => b.total - a.total);

    const totalPorMes = meses.map((key) => ({
      mes: key,
      label: monthLabel(key),
      value: linhas.reduce((sum, l) => sum + (l.byMonth[key] || 0), 0),
    }));
    const totalLast = lastMes ? totalPorMes.find((m) => m.mes === lastMes)?.value || 0 : 0;
    const totalPrev = prevMes ? totalPorMes.find((m) => m.mes === prevMes)?.value || 0 : 0;
    const variacao = prevMes && totalPrev > 0 ? ((totalLast - totalPrev) / totalPrev) * 100 : null;

    return { meses, linhas, clientesAtivos: linhas.length, compraramUltimoMes, variacao, totalPorMes, contagemStatus };
  }, [vendas, clientes, dataInicial, dataFinal, vendedor, clienteFiltro, produtoFiltro, allowedPeople]);

  const maxMes = Math.max(...totalPorMes.map((m) => m.value), 1);
  const porPagina = 10;
  const totalPaginas = Math.max(1, Math.ceil(linhas.length / porPagina));
  const inicio = (pagina - 1) * porPagina;
  const linhasPaginadas = linhas.slice(inicio, inicio + porPagina);
  useEffect(() => setPagina(1), [dataInicial, dataFinal, vendedor, clienteFiltro, produtoFiltro, allowedPeople]);
  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  return (
    <div className="space-y-4 md:space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-semibold text-foreground-950">Análise de Vendas (kg)</h2>
        <span className="text-xs text-foreground-500">{loading ? 'Carregando...' : 'Dados reais — livro-razão CIGAM'}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-white rounded-xl border border-background-200/70 p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
            <i className="ri-team-line text-primary-600 text-lg" />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground-700">Número de clientes ativos</p>
            <p className="text-2xl font-bold text-foreground-950 font-tabular">{clientesAtivos}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200/70 p-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
            <i className="ri-user-follow-line text-accent-600 text-lg" />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground-700">Clientes que compraram no último mês</p>
            <p className="text-2xl font-bold text-foreground-950 font-tabular">{compraramUltimoMes}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200/70 p-4 flex items-center gap-3">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${variacao === null ? 'bg-background-100' : variacao >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
            <i className={`${variacao === null ? 'ri-subtract-line text-foreground-500' : variacao >= 0 ? 'ri-arrow-up-line text-emerald-600' : 'ri-arrow-down-line text-red-600'} text-lg`} />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground-700">% Variação vendas último mês</p>
            <p className={`text-2xl font-bold font-tabular ${variacao === null ? 'text-foreground-950' : variacao >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {variacao === null ? '—' : `${variacao >= 0 ? '+' : ''}${variacao.toFixed(0)}%`}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-4 md:gap-5">
        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">Vendas em kg por mês</h3>
          {totalPorMes.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-foreground-700">Sem dados para os filtros selecionados.</div>
          ) : (
            <div className="flex items-end gap-3 h-40">
              {totalPorMes.map((mes) => (
                <div key={mes.mes} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
                  <span className="text-[11px] font-bold text-foreground-950 font-tabular">{formatKg(mes.value)}</span>
                  <div
                    className="w-full bg-primary-500 rounded-t-md transition-all duration-700 ease-out"
                    style={{ height: `${Math.max((mes.value / maxMes) * 100, 3)}%` }}
                  />
                  <span className="text-[11px] font-semibold text-foreground-600">{mes.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-background-200/70 p-5">
          <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">Status da carteira</h3>
          <div className="space-y-3">
            {(['amarelo', 'vermelho', 'verde'] as Status[]).map((status) => (
              <div key={status} className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full ${STATUS_META[status].dot} bg-opacity-15 flex items-center justify-center shrink-0`}>
                  <i className={`${STATUS_META[status].icon} ${STATUS_META[status].text} text-lg`} />
                </span>
                <span className="text-2xl font-bold text-foreground-950 font-tabular w-10 text-right">{contagemStatus[status]}</span>
                <span className="text-sm text-foreground-700">{STATUS_META[status].label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-200/70">
          <h3 className="font-heading text-base font-semibold text-foreground-950">Vendas por cliente (kg)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-background-100/50 text-left">
                <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide">Cliente</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide">UF</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide">Vendedor</th>
                {totalPorMes.map(({ mes, label }) => (
                  <th key={mes} className="px-3 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide text-right">{label}</th>
                ))}
                <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide text-right">Total</th>
                <th className="px-4 py-2.5 text-xs font-semibold text-foreground-600 uppercase tracking-wide text-center">Indicador</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/60">
              {linhas.length === 0 && (
                <tr><td colSpan={totalPorMes.length + 5} className="px-4 py-6 text-center text-foreground-500">Sem dados para os filtros selecionados.</td></tr>
              )}
              {linhasPaginadas.map((linha) => (
                <tr key={linha.codigo} className="hover:bg-background-100/40 transition-colors">
                  <td className="px-4 py-3 text-foreground-950 max-w-[220px] truncate" title={linha.nome}>{linha.nome}</td>
                  <td className="px-4 py-3 text-foreground-700">{linha.uf}</td>
                  <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{linha.responsavel || '—'}</td>
                  {totalPorMes.map(({ mes }) => (
                    <td key={mes} className="px-3 py-3 text-right font-tabular text-foreground-700">{linha.byMonth[mes] ? formatKg(linha.byMonth[mes]) : '—'}</td>
                  ))}
                  <td className="px-4 py-3 text-right font-semibold text-foreground-950 font-tabular">{formatKg(linha.total)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block w-3 h-3 rounded-full ${STATUS_META[linha.indicador].dot}`} title={STATUS_META[linha.indicador].label} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {linhas.length > 0 && <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200/70 px-5 py-3">
          <span className="text-xs text-foreground-600">Exibindo {inicio + 1}–{Math.min(inicio + porPagina, linhas.length)} de {linhas.length} clientes</span>
          <div className="flex items-center gap-1.5">
            <button type="button" disabled={pagina === 1} onClick={() => setPagina((atual) => Math.max(1, atual - 1))} className="rounded-lg border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-700 disabled:cursor-not-allowed disabled:opacity-40">Anterior</button>
            <span className="min-w-20 text-center text-xs font-semibold text-foreground-800">{pagina} de {totalPaginas}</span>
            <button type="button" disabled={pagina === totalPaginas} onClick={() => setPagina((atual) => Math.min(totalPaginas, atual + 1))} className="rounded-lg border border-background-200 px-3 py-1.5 text-xs font-semibold text-foreground-700 disabled:cursor-not-allowed disabled:opacity-40">Próxima</button>
          </div>
        </div>}
      </div>
    </div>
  );
}
